import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  signOut, GoogleAuthProvider, signInWithPopup, deleteUser,
} from 'firebase/auth';
import { useFormik, Form, FormikProvider } from 'formik';
import { useTheme } from '@mui/material/styles';
// material
import {
  Link,
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Box,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { LoadingButton } from '@mui/lab';
// component
import { connectStorageEmulator } from 'firebase/storage';
import Typography from '../../../theme/overrides/Typography';
import { getFirstEnabledModule, usePrivilege } from '../../../PrivilegeContext';
import Iconify from '../../../components/Iconify';
import SnackBar from '../../../components/Snackbar';
import { auth } from '../../../firebase';
import { APIGetAdminUserAndPrivileges } from '../../../utils/apiCalled';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [mainMsg, setMainMsg] = useState('');
  const [userFirebase, setUserFirebase] = useState(null);
  const { setRole, setModules } = usePrivilege();

  const theme = useTheme();

  // this to called the GetAdminUser API.
  const fetchAdminUser = async (user) => {
    try {
      const result = await APIGetAdminUserAndPrivileges(setLoading, user.email);
      const {
        _id, firstName, lastName, email, operatorAssignments, privileges,
      } = result.data;
      const { role, module } = privileges;
      if (role) {
        setRole(role);
        setModules(module);
        Cookies.set('role', role);
        Cookies.set('name', `${firstName} ${lastName}`);
        Cookies.set('email', email);
        Cookies.set('userId', _id);
        // Store operatorAssignments as JSON string if it's an array/object
        Cookies.set('operatorAssigned', Array.isArray(operatorAssignments) || typeof operatorAssignments === 'object'
          ? JSON.stringify(operatorAssignments)
          : operatorAssignments);
        Cookies.set('isLogin', true);

        // Check if user has operators assigned
        const hasOperators = operatorAssignments && (
          (Array.isArray(operatorAssignments) && operatorAssignments.length > 0)
          || (typeof operatorAssignments === 'string' && operatorAssignments.trim().length > 0)
        );

        if (hasOperators) {
          // Navigate to operator selection page
          navigate('/operator-select', { replace: true });
        } else {
          // No operators assigned, navigate directly to dashboard
          const firstModule = getFirstEnabledModule(module);
          navigate(`/dashboard/${firstModule.key}`, { replace: true });
        }
      }
    } catch (err) {
      setLoading(false);
      setOpen(true);
      setErrMsg(err);
      setMainMsg('Error');
    }
  };

  // to called the GoogleSignIn function
  const googleSignIn = async () => {
    if (loading) return; // prevent multiple clicks
    setLoading(true);

    const provider = new GoogleAuthProvider();

    signInWithPopup(auth, provider)
      .then((result) => {
        const { user } = result;
        fetchAdminUser(user);
        Cookies.set('isLogin', true);
        setLoading(false);
      })
      .catch((error) => {
        setOpen(true);
        setMainMsg('Error Encountered');
        setErrMsg(`${error.code} : ${error.message}`);
        setLoading(false);
      });
  };

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <Box>
      <SnackBar alert="error" handleClose={() => setOpen(false)} open={open} errorMsg={errMsg} mainMsg={mainMsg} />
      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="primary"
        sx={{
          border: '2px solid',
          borderColor: theme.palette.grey[300],
          borderRadius: '12px',
          color: theme.palette.grey[700],
        }}
        onClick={() => googleSignIn()}
      >
        <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
          <img height={24} src="/static/icons/google.svg" alt="google" />
          <div style={{ margin: '0px 0px 0px 16px' }}>Sign in with Google</div>
        </div>
      </LoadingButton>
    </Box>
  );
}
